// A room: your friends, one code, and every game you want to settle.
//
// The room is the thing that lasts. People join it by playing, games are added
// to it one at a time, and each game keeps its own table while the room keeps
// the overall count of who topped what. Nothing here needs an account — a name
// typed once is remembered on the device, and everything else lives on the
// server, so the room is the same on every phone that opens the link.

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getSavedName, saveName, deviceToken } from '../services/challengeService';
import { useUsername } from '../hooks/useUsername';
import animeConfig from '../config/animeConfig';
import { trackEvent } from '../services/analyticsService';
import './Room.css';
import { CHARACTERS } from './JigsawPuzzle';
import RaceRules from '../components/RaceRules/RaceRules';

const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

const Room = () => {
  const { code: rawCode } = useParams();
  const code = (rawCode || '').toUpperCase();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [name, setName] = useState(getSavedName);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState('');
  const [copied, setCopied] = useState(false);
  const [openMember, setOpenMember] = useState(null);
  // A signed-in player joins under their account name unless they type another.
  const { username } = useUsername();
  useEffect(() => { if (!draft && username) setDraft(username); }, [username]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    try {
      // The device token says whether this phone made the room (owner tools).
      const res = await fetch(`/api/room?code=${encodeURIComponent(code)}`, { headers: { 'x-room-token': deviceToken() } });
      const json = await res.json();
      if (!json.success) { setError(json.error || 'That room does not exist.'); return; }
      setData(json);
    } catch (e) {
      setError('Could not reach the room. Check your connection and try again.');
    }
  }, [code]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (data) {
      trackEvent('room_opened', {
        code,
        members: data.members.length,
        games: data.games.length,
        event_category: 'Room',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data && data.room.code]);

  const join = async () => {
    const chosen = draft.trim();
    if (!chosen) return;
    setBusy('join');
    try {
      const res = await fetch('/api/room', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'join', code, name: chosen, token: deviceToken() }),
      });
      const json = await res.json();
      if (!json.success) { setError(json.closed ? 'This room is closed.' : (json.error || 'Could not join.')); return; }
      if (json.success) {
        setData(json);
        saveName(chosen);
        setName(chosen);
        trackEvent('room_joined', { code, event_category: 'Room' });
      }
    } finally {
      setBusy('');
    }
  };

  // Picking a game hands everyone the same match, making it the first time.
  const play = async (game, path) => {
    // The puzzle is picked on its own page (character + size); a puzzle already
    // in the room opens with the same character and size chosen.
    if (game === 'puzzle' || game.startsWith('puzzle-')) {
      const pick = game.startsWith('puzzle-') ? `&p=${game.slice(7)}` : '';
      navigate(`/puzzle?room=${code}${pick}`);
      return;
    }
    setBusy(game);
    try {
      const res = await fetch('/api/room', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'play', code, game, name, token: deviceToken() }),
      });
      const json = await res.json();
      if (!json.success) { setError(json.error || 'Could not start that game.'); return; }
      trackEvent('room_play', { code, game, event_category: 'Room' });
      navigate(`${path}?r=${json.code}&room=${code}`);
    } finally {
      setBusy('');
    }
  };

  // Owner tools. Nothing is deleted: hidden players and a closed room can be
  // brought back, and the server checks this phone made the room.
  const ownerDo = async (action, who, question) => {
    if (question && !window.confirm(question)) return;
    setBusy(action);
    try {
      const res = await fetch('/api/room', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, code, name: who, token: deviceToken() }),
      });
      const json = await res.json();
      if (json.success) { setData(json); setOpenMember(null); trackEvent(`room_${action}`, { code, event_category: 'Room' }); }
    } finally {
      setBusy('');
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`https://www.onepiecetrivia.com/room/${code}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 4000);
    } catch (e) { /* they can select it by hand */ }
  };

  const seaBackground = {
    backgroundImage: `linear-gradient(to bottom, rgba(5,8,20,0.82), rgba(5,8,20,0.94)), url(${animeConfig.onePiece.backgroundImage})`,
  };

  if (error) {
    return (
      <div className="room-wrap" style={seaBackground}>
        <div className="room-card room-empty">
          <h1>Room {code}</h1>
          <p>{error}</p>
          <Link className="room-btn room-btn-primary" to="/bounty-battle">Start a new one</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="room-wrap" style={seaBackground}>
        <div className="room-card room-empty"><p><span className="room-spin" aria-hidden="true" /> Opening the room…</p></div>
      </div>
    );
  }

  const { room, members, games, catalogue, isOwner, hidden = [] } = data;
  const ownerKey = String(room.createdBy || '').toLowerCase();
  const resultsOf = (who) => games.map((g) => ({ g, p: g.players.find((x) => x.name.toLowerCase() === who.toLowerCase()) }));
  const puzzleName = Object.fromEntries(CHARACTERS);
  const puzzleLabel = (g) => (g.puzzle ? `Jigsaw · ${puzzleName[g.puzzle.slug] || g.puzzle.slug} · ${g.puzzle.pieces} pieces` : g.label);
  const mine = name.toLowerCase();
  const played = new Set(games.map((g) => g.game));
  const unplayed = catalogue.filter((c) => c.game === 'puzzle' || !played.has(c.game));
  const totalGames = games.reduce((n, g) => n + g.players.reduce((a, p) => a + p.attempts, 0), 0);

  return (
    <div className="room-wrap" style={seaBackground}>
      <Helmet>
        <title>{room.name ? `${room.name} — One Piece Room` : `Room ${room.code} — One Piece Trivia`}</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="room-card room-paper">
        <div className="room-paper-pins" aria-hidden="true"><span /><span /></div>
        <div className="room-head">
          <div>
            <p className="room-eyebrow">The crew of</p>
            <h1 className="room-title">{room.name || 'Your room'}</h1>
            {room.createdBy && <p className="room-by">Made by <b>{room.createdBy}</b> 👑{isOwner ? ' · that’s you' : ''}</p>}
            <p className="room-sub">
              {members.length === 1 ? '1 player' : `${members.length} players`}
              {' · '}
              {games.length === 1 ? '1 game' : `${games.length} games`}
              {' · '}
              {totalGames === 1 ? '1 round played' : `${totalGames} rounds played`}
            </p>
          </div>
          <span className="room-code">{room.code}</span>
        </div>

        <div className="room-invite">
          <div className="room-link" aria-label="Room link">
            {`onepiecetrivia.com/room/${room.code}`}
          </div>
          <button className="room-btn room-btn-copy" onClick={copyLink}>{copied ? '✓ Copied' : 'Copy'}</button>
        </div>

        {room.closed && <p className="room-closed">🔒 This room is closed. The results stay here.</p>}

        {!name && !room.closed && (
          <div className="room-join">
            <label htmlFor="room-name">{games.some((g) => g.puzzle) ? 'You’re invited to race · type your name, then Join' : 'Your name in this room'}</label>
            <div className="room-join-row">
              <input
                id="room-name"
                className="room-input"
                maxLength={18}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="e.g. Luffy"
              />
              <button
                className="room-btn room-btn-primary"
                onClick={join}
                disabled={!draft.trim() || busy === 'join'}
              >
                {busy === 'join' ? 'Joining…' : 'Join'}
              </button>
            </div>
          </div>
        )}

        {members.length > 0 && (
          <div className="room-members">
            {members.map((m) => (
              <button type="button" key={m.name}
                className={`room-member ${m.name.toLowerCase() === mine ? 'is-me' : ''} ${openMember === m.name ? 'is-open' : ''}`}
                onClick={() => setOpenMember(openMember === m.name ? null : m.name)}>
                {m.name.toLowerCase() === ownerKey && <span className="room-crown" aria-label="Owner">👑</span>}
                {m.name}
                {m.wins > 0 && <em>{m.wins === 1 ? '1 win' : `${m.wins} wins`}</em>}
              </button>
            ))}
          </div>
        )}

        {openMember && (
          <div className="room-player">
            <div className="room-player-head">
              <strong>{openMember}</strong>
              {openMember.toLowerCase() === ownerKey && <span className="room-player-tag">Room owner</span>}
              {openMember.toLowerCase() === mine && <span className="room-player-tag">You</span>}
            </div>
            {resultsOf(openMember).map(({ g, p }) => (
              <div key={g.matchCode} className="room-player-row">
                <div className="room-player-game">
                  <span>{puzzleLabel(g)}</span>
                  {/* Every finished run, oldest first: the best is marked, and an odd jump shows */}
                  {p && p.runs && p.runs.length > 0 && (
                    <span className="room-runs">{p.runs.map((t, i) => (
                      <em key={i} className={t === Math.min(...p.runs) ? 'is-best' : ''}>{formatTime(t)}</em>
                    ))}</span>
                  )}
                </div>
                <b>{!p ? 'not played' : g.puzzle ? (p.score >= g.rounds ? `✓ ${formatTime(p.totalTime)}` : `${p.score}/${g.rounds}`) : `${p.score}/${g.rounds} · ${formatTime(p.totalTime)}`}</b>
              </div>
            ))}
            {isOwner && openMember.toLowerCase() !== ownerKey && (
              <button type="button" className="room-btn room-btn-ghost room-remove" disabled={!!busy}
                onClick={() => ownerDo('hide', openMember, `Remove ${openMember} from this room? Their results are kept and you can bring them back.`)}>
                Remove from room
              </button>
            )}
          </div>
        )}

        {isOwner && hidden.length > 0 && (
          <p className="room-hidden">Removed: {hidden.map((h, i) => (
            <span key={h}>{i > 0 && ', '}{h} <button type="button" className="room-link" onClick={() => ownerDo('unhide', h)}>bring back</button></span>
          ))}</p>
        )}
      </div>

      {games.map((g) => (
        <div className="room-card" key={g.matchCode}>
          <div className="room-game-head">
            <h2 className="room-game-title">{puzzleLabel(g)}</h2>
            {!room.closed && (
            <button
                className="room-btn room-btn-primary"
                onClick={() => (name ? play(g.game, g.path) : document.getElementById('room-name')?.focus())}
                disabled={busy === g.game}
                title={name ? '' : 'Type your name and press Join first'}
              >
                {busy === g.game ? 'Opening…' : !name ? 'Join first ↑' : g.players.some((p) => p.name.toLowerCase() === mine) ? 'Play again' : 'Play'}
              </button>
            )}
          </div>

          {g.puzzle && <RaceRules className="room-race-rules" />}
          {g.players.length === 0 ? (
            <p className="room-note">Nobody has played this one yet.</p>
          ) : (
            <ol className="room-list">
              {g.players.map((p, i) => (
                <li key={p.name} className={`room-row ${p.name.toLowerCase() === mine ? 'is-me' : ''}`}>
                  <span className="room-rank">{i + 1}</span>
                  <span className="room-name">{p.name}</span>
                  <span className="room-score">{g.puzzle ? (p.score >= g.rounds ? '✓ done' : p.score === 0 ? 'ready' : `${p.score}/${g.rounds} joined`) : `${p.score}/${g.rounds}`}</span>
                  <span className="room-time">{formatTime(p.totalTime)}</span>
                  <span className="room-tries">{p.attempts === 0 ? 'playing' : p.attempts === 1 ? '1 try' : `${p.attempts} tries`}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      ))}

      {/* Only the room's owner adds games; an invited friend came to play */}
      {isOwner && unplayed.length > 0 && !room.closed && (
        <details className="room-card room-more" open={isOwner && games.length === 0}>
          {/* Folded for invited friends: they came to race, not to set the room up */}
          <summary className="room-game-title">{games.length ? 'Add another game' : 'Add a game'}</summary>
          <p className="room-note">Everyone in the room gets the same questions, in the same order.</p>
          <div className="room-add">
            {unplayed.map((c) => (
              <button
                key={c.game}
                className="room-btn room-btn-ghost"
                onClick={() => play(c.game, c.path)}
                disabled={busy === c.game}
              >
                {busy === c.game ? 'Opening…' : `+ ${c.label}`}
              </button>
            ))}
          </div>
        </details>
      )}
      {isOwner && (
        <div className="room-owner-bar">
          {room.closed ? (
            <button type="button" className="room-btn room-btn-ghost" disabled={!!busy} onClick={() => ownerDo('reopen')}>Reopen room</button>
          ) : (
            <button type="button" className="room-btn room-btn-ghost" disabled={!!busy}
              onClick={() => ownerDo('close', null, 'Close this room? Nobody can join or play, the results stay, and you can reopen it.')}>Close room</button>
          )}
        </div>
      )}
    </div>
  );
};

export default Room;
